import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../utils/api';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CorrectIcon,
  Cancel as WrongIcon,
  Quiz as QuizIcon,
  ArrowBack as BackIcon,
  Replay as RetakeIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface QuizResponse {
  id: number;
  question_id: number;
  question_type: string;
  question_text: string;
  question_options: { label: string; text: string }[] | null;
  correct_answer: string | null;
  explanation: string | null;
  student_answer: string | null;
  selected_option: string | null;
  is_correct: boolean | null;
  points_earned: number | null;
  points_possible: number | null;
  ai_suggested_score: number | null;
  ai_feedback: string | null;
  grading_method: string | null;
}

interface Attempt {
  id: number;
  attempt_number: number;
  status: string;
  total_score: number | null;
  total_possible: number | null;
  percentage: number | null;
  submitted_at: string | null;
  responses: QuizResponse[];
}

const StudentQuizResults: React.FC = () => {
  const { quizAssignmentId, attemptId } = useParams<{ quizAssignmentId: string; attemptId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState<Attempt | null>(null);

  useEffect(() => {
    if (attemptId) fetchResults();
  }, [attemptId]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const resp = await axios.get(
        `${API_BASE_URL}/api/student/quiz-attempts/${attemptId}/results`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.data.success) {
        setAttempt(resp.data.attempt);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!attempt) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Results not found'}</Alert>
      </Box>
    );
  }

  const score = attempt.total_score ?? 0;
  const possible = attempt.total_possible ?? 1;
  const pct = attempt.percentage ?? 0;
  const passed = pct >= 70;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: isMobile ? 2 : 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/student/assignments')} size="small">
          {t('common.back')}
        </Button>
      </Box>

      {/* Score summary */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          textAlign: 'center',
          bgcolor: passed ? '#e8f5e9' : '#fff3e0',
          border: `2px solid ${passed ? '#4caf50' : '#ff9800'}`,
        }}
      >
        <QuizIcon sx={{ fontSize: 48, color: passed ? '#2e7d32' : '#e65100', mb: 1 }} />
        <Typography variant="h4" fontWeight={700} sx={{ color: passed ? '#2e7d32' : '#e65100' }}>
          {score} / {possible}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {pct}%
        </Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            mt: 2,
            height: 10,
            borderRadius: 5,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': { bgcolor: passed ? '#4caf50' : '#ff9800' },
          }}
        />
        {attempt.submitted_at && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('quiz.results.submittedAt')}: {new Date(attempt.submitted_at).toLocaleString()}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Per-question breakdown */}
      {attempt.responses.map((resp, idx) => {
        const earned = resp.points_earned ?? 0;
        const pts = resp.points_possible ?? 1;
        const correct = resp.is_correct;

        return (
          <Paper
            key={resp.id}
            sx={{
              p: 2.5,
              mb: 2,
              borderLeft: `4px solid ${correct ? '#4caf50' : '#f44336'}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('quiz.results.questionNum', { num: idx + 1 })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {correct ? (
                  <CorrectIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : (
                  <WrongIcon sx={{ color: '#f44336', fontSize: 20 }} />
                )}
                <Chip
                  label={`${earned} / ${pts}`}
                  size="small"
                  sx={{
                    bgcolor: correct ? '#e8f5e9' : '#ffebee',
                    color: correct ? '#2e7d32' : '#c62828',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
              {resp.question_text}
            </Typography>

            {/* Student's answer */}
            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 1.5, mb: 1 }}>
              <Typography variant="caption" fontWeight={600}>{t('quiz.results.yourAnswer')}:</Typography>
              <Typography variant="body2">
                {resp.question_type === 'multiple_choice'
                  ? resp.question_options?.find((o) => o.label === resp.selected_option)
                    ? `${resp.selected_option}) ${resp.question_options.find((o) => o.label === resp.selected_option)?.text}`
                    : resp.selected_option || t('quiz.results.noAnswer')
                  : resp.student_answer || t('quiz.results.noAnswer')}
              </Typography>
            </Box>

            {/* Correct answer */}
            {!correct && resp.correct_answer && (
              <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 1, p: 1.5, mb: 1 }}>
                <Typography variant="caption" fontWeight={600} color="#2e7d32">
                  {t('quiz.correctAnswer')}:
                </Typography>
                <Typography variant="body2" color="#2e7d32">
                  {resp.question_type === 'multiple_choice' && resp.question_options
                    ? `${resp.correct_answer}) ${resp.question_options.find((o) => o.label === resp.correct_answer)?.text || ''}`
                    : resp.correct_answer}
                </Typography>
              </Box>
            )}

            {/* AI feedback for short answer */}
            {resp.grading_method === 'ai' && resp.ai_feedback && (
              <Box sx={{ bgcolor: '#fff3e0', borderRadius: 1, p: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <InfoIcon sx={{ fontSize: 16, color: '#e65100' }} />
                  <Typography variant="caption" fontWeight={600} color="#e65100">
                    {t('quiz.results.aiFeedback')}:
                  </Typography>
                </Box>
                <Typography variant="body2" color="#e65100">
                  {resp.ai_feedback}
                </Typography>
              </Box>
            )}

            {/* Explanation */}
            {resp.explanation && (
              <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1, p: 1.5 }}>
                <Typography variant="caption" fontWeight={600} color="#1565c0">
                  {t('quiz.explanation')}:
                </Typography>
                <Typography variant="body2" color="#1565c0">
                  {resp.explanation}
                </Typography>
              </Box>
            )}
          </Paper>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/student/assignments')}
          aria-label={t('quiz.results.backToAssignments')}
        >
          {t('quiz.results.backToAssignments')}
        </Button>
      </Box>
    </Box>
  );
};

export default StudentQuizResults;
